// ─────────────────────────────────────────────────────────────────────────────
// Deterministic agent addressing for the REAL DOM.
//
// Owner requirement (2026-09-21):
//   "It needs to actually be able to control the DOM and have access to all of
//    its assets... It just knows what page we're on. It's not able to tell me
//    all of the assets on the page. It should know all the elements/items on the
//    page. The site should be coded and labeled properly with proper IDs so the
//    agent can know exactly what each element is."
//
// Hand-tagging every control across ~60 pages with `data-agent-id` is brittle
// and always lags the UI (the Opportunity Scanner page had ZERO tags, so the
// agent could not find its Refresh button). Instead we DERIVE a stable id for
// every interactive element on the client, at runtime, and write it back onto
// the element as `data-agent-id` — so the same resolver (`resolveAgentId`) and
// the same screen report work unchanged, and every page becomes addressable
// without touching it.
//
// Authors can still override any element with an explicit `data-agent-id`; an
// explicit id is never replaced. Auto ids are namespaced `auto.` so the two are
// always distinguishable.
// ─────────────────────────────────────────────────────────────────────────────

export type AgentKind = 'button' | 'input' | 'select' | 'link' | 'control' | 'info';

export interface TaggedElement {
  id: string;
  label: string;
  kind: AgentKind;
  actions: string[];
  explicit: boolean;
  value?: string;
  disabled?: boolean;
}

export const AUTO_PREFIX = 'auto.';

/** Elements a user can actually operate. */
const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="combobox"]',
  '[role="searchbox"]',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

/** Readable, non-interactive landmarks so the agent can describe the page. */
const INFO_SELECTOR = 'h1,h2,h3,h4,[role="heading"],[data-agent-info]';

/** Assigned ids, so a re-render never renames an element mid-flight. */
const ASSIGNED = new WeakMap<Element, string>();

/** Tidy a raw accessible name for display + id slugs: collapse whitespace and
 *  drop leading decorative glyphs/emoji (the debrief chips read "🛡️\nApprovals\n8"). */
export function tidyLabel(raw: string): string {
  let text = (raw || '').replace(/\s+/g, ' ').trim();
  text = text.replace(/^[^\p{L}\p{N}]+/u, '').trim();
  return text;
}

function slugify(text: string, max = 48): string {
  const slug = (text || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max);
  return slug || 'unnamed';
}

/** Route → id namespace. `/app/opportunity-scanner` → `opportunity-scanner`. */
export function routeKey(route: string): string {
  const parts = (route || '/').split('/').filter(Boolean);
  if (parts[0] === 'app') parts.shift();
  return parts.length ? slugify(parts.join('-')) : 'home';
}

/** Small stable hash of an element's position in the tree (collision breaker). */
function pathHash(el: Element): string {
  const parts: number[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && node.parentElement && depth < 12) {
    parts.push(Array.prototype.indexOf.call(node.parentElement.children, node));
    node = node.parentElement;
    depth += 1;
  }
  let h = 5381;
  for (const p of parts) h = ((h * 33) ^ (p + 7)) >>> 0;
  return h.toString(36).slice(0, 4);
}

/** Best-effort accessible name, in the order assistive tech uses it. */
export function accessibleName(el: HTMLElement): string {
  const attr = (name: string) => (el.getAttribute(name) || '').trim();
  const labelledBy = attr('aria-labelledby');
  if (labelledBy) {
    const target = document.getElementById(labelledBy);
    if (target?.textContent?.trim()) return target.textContent.trim();
  }
  const aria = attr('aria-label');
  if (aria) return aria;
  if (el.id) {
    const label = document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(el.id)}"]`);
    if (label?.textContent?.trim()) return label.textContent.trim();
  }
  const closestLabel = el.closest('label');
  if (closestLabel?.textContent?.trim()) return closestLabel.textContent.trim();
  const title = attr('title');
  if (title) return title;
  const placeholder = attr('placeholder');
  if (placeholder) return placeholder;
  const alt = el.querySelector('img[alt]')?.getAttribute('alt')?.trim();
  if (alt) return alt;
  const text = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return text;
  const name = attr('name');
  if (name) return name;
  return '';
}

function classify(el: HTMLElement): { kind: AgentKind; actions: string[] } {
  const tag = el.tagName.toLowerCase();
  const role = (el.getAttribute('role') || '').toLowerCase();
  const type = (el as HTMLInputElement).type?.toLowerCase?.() || '';
  if (tag === 'select' || role === 'combobox' || role === 'listbox') {
    return { kind: 'select', actions: ['select', 'highlight', 'scrollTo'] };
  }
  if (tag === 'input' || tag === 'textarea' || role === 'searchbox' || role === 'textbox') {
    if (['checkbox', 'radio', 'switch'].includes(type) || role === 'checkbox' || role === 'switch' || role === 'radio') {
      return { kind: 'control', actions: ['click', 'highlight', 'scrollTo'] };
    }
    return { kind: 'input', actions: ['type', 'highlight', 'scrollTo'] };
  }
  if (tag === 'a' || role === 'link') return { kind: 'link', actions: ['click', 'highlight', 'scrollTo'] };
  if (tag === 'button' || role === 'button' || role === 'tab' || role === 'menuitem' || role === 'option') {
    return { kind: 'button', actions: ['click', 'highlight', 'scrollTo'] };
  }
  if (el.isContentEditable) return { kind: 'input', actions: ['type', 'highlight', 'scrollTo'] };
  return { kind: 'control', actions: ['click', 'highlight', 'scrollTo'] };
}

/** Computed-style visibility. Size filtering happens in the screen report. */
function stylesSayVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

/**
 * Tag every addressable element under *root* for *route*.
 *
 * Idempotent: an element that already has an id (explicit or auto) keeps it.
 * Returns the metadata for everything tagged, in document order.
 */
export function tagInteractiveElements(route: string, root?: ParentNode): TaggedElement[] {
  if (typeof document === 'undefined') return [];
  const scope: ParentNode = root || document;
  const ns = routeKey(route);
  const out: TaggedElement[] = [];
  const used = new Set<string>();
  const reserved = new Set<string>();

  // 1) Explicit author tags always win — reserve them first.
  scope.querySelectorAll<HTMLElement>('[data-agent-id]').forEach((el) => {
    const id = el.getAttribute('data-agent-id') || '';
    if (id) reserved.add(id);
  });

  const take = (el: HTMLElement, explicit: boolean, explicitId?: string): void => {
    const label = tidyLabel(accessibleName(el));
    const { kind, actions } = classify(el);
    let id = explicitId || ASSIGNED.get(el) || '';
    if (!id) {
      const base = `${AUTO_PREFIX}${ns}.${slugify(label || el.tagName)}`;
      id = !used.has(base) && !reserved.has(base) ? base : `${base}~${pathHash(el)}`;
    }
    if (used.has(id)) return; // duplicate in this pass
    used.add(id);
    ASSIGNED.set(el, id);
    if (!explicit && el.getAttribute('data-agent-id') !== id) {
      el.setAttribute('data-agent-id', id);
      el.setAttribute('data-agent-auto', '1');
    }
    out.push({
      id,
      label: label || id,
      kind,
      actions,
      explicit,
      value: kind === 'input' || kind === 'select' ? (el as HTMLInputElement).value || undefined : undefined,
      disabled: (el as HTMLButtonElement).disabled || undefined,
    });
  };

  scope.querySelectorAll<HTMLElement>('[data-agent-id]').forEach((el) => {
    const id = el.getAttribute('data-agent-id') || '';
    if (id && stylesSayVisible(el)) take(el, true, id);
  });

  scope.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR).forEach((el) => {
    if (el.hasAttribute('data-agent-id')) return;
    if (!stylesSayVisible(el)) return;
    // Skip controls that are only a label wrapper for a nested input.
    if (el.tagName.toLowerCase() === 'label') return;
    take(el, false);
  });

  scope.querySelectorAll<HTMLElement>(INFO_SELECTOR).forEach((el) => {
    if (el.hasAttribute('data-agent-id')) return;
    if (!stylesSayVisible(el)) return;
    const label = tidyLabel(accessibleName(el));
    if (!label) return;
    const base = `${AUTO_PREFIX}${ns}.${slugify(label)}`;
    const id = !used.has(base) && !reserved.has(base) ? base : `${base}~${pathHash(el)}`;
    if (used.has(id)) return;
    used.add(id);
    ASSIGNED.set(el, id);
    el.setAttribute('data-agent-id', id);
    el.setAttribute('data-agent-info', '1');
    out.push({ id, label, kind: 'info', actions: ['highlight', 'scrollTo'], explicit: false });
  });

  return out;
}

/** Last-resort live lookup: re-tag, then retry the attribute query. */
export function resolveAutoId(id: string): HTMLElement | null {
  if (!id.startsWith(AUTO_PREFIX)) return null;
  tagInteractiveElements(window.location.pathname);
  return document.querySelector<HTMLElement>(`[data-agent-id="${CSS.escape(id)}"]`);
}
